/**
 * `SpellQueueWindowProvider` / `useSpellQueueWindow` (#826) — the single LIVE
 * source of truth for the spell-queue window (#797), in milliseconds.
 *
 * WHY A PROVIDER (and not just `prefs.ts`): the preference has been
 * persistable since #797 — `saveSpellQueueWindowMs` shipped with the load
 * side and had ZERO callers, so the value could only ever be the default
 * unless a player hand-edited localStorage. Adding a settings control needs
 * two things prefs alone can't give: somewhere for the CURRENT value to live
 * while the settings screen renders it, and a way for a change to reach
 * `InputSurface`'s already-mounted `SpellQueue` without a page reload. That is
 * exactly `wasdStrafeMode.tsx`'s job for its own preference, so this is its
 * structural twin — same persistence seam, same "persist first, then update
 * live state" write order, same throwing `use*` accessor.
 *
 * CLAMPED ON THE WAY IN AND OUT: `loadSpellQueueWindowMs` clamps what it reads
 * and `setSpellQueueWindowMs` clamps what it writes (both through
 * `clampSpellQueueWindowMs`), so no consumer can ever observe a window outside
 * `[0, 210]` ms — `0` is the documented kill switch, not an invalid value.
 *
 * PURELY CLIENT-SIDE INPUT TIMING. The window changes WHEN a cast input is
 * delivered, never WHETHER it is legal — no protocol, sim, content, hashed
 * state or `SIM_VERSION` involvement (see `spellQueue.ts`'s header).
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { SPELL_QUEUE_WINDOW_MS_DEFAULT, clampSpellQueueWindowMs } from "./spellQueue.js";
import {
  loadSpellQueueWindowMs as loadFromPrefs,
  saveSpellQueueWindowMs as saveToPrefs,
} from "../prefs.js";

/** Persistence seam — injectable for tests. */
export interface SpellQueueWindowPersistence {
  readonly load: () => number;
  readonly save: (ms: number) => void;
}

/** Default persistence: the per-browser prefs localStorage boundary — the
 *  first production caller `saveSpellQueueWindowMs` has ever had (#826). */
export const PREFS_SPELL_QUEUE_WINDOW_PERSISTENCE: SpellQueueWindowPersistence = {
  load: () => loadFromPrefs(),
  save: (ms) => {
    saveToPrefs(ms);
  },
};

export interface SpellQueueWindowContextValue {
  /** The effective window in ms, always within `[0, 210]`. */
  readonly spellQueueWindowMs: number;
  /** Persist a new window and refresh every consumer live. Clamped. */
  readonly setSpellQueueWindowMs: (ms: number) => void;
}

const SpellQueueWindowCtx = createContext<SpellQueueWindowContextValue | null>(null);

export function SpellQueueWindowProvider({
  children,
  persistence = PREFS_SPELL_QUEUE_WINDOW_PERSISTENCE,
}: {
  readonly children: ReactNode;
  readonly persistence?: SpellQueueWindowPersistence;
}): JSX.Element {
  const [value, setValue] = useState<number>(() => clampSpellQueueWindowMs(persistence.load()));

  const setSpellQueueWindowMs = useCallback(
    (next: number) => {
      // Clamp ONCE here so the persisted value and the live value can never
      // disagree (`saveSpellQueueWindowMs` clamps too — this makes the value
      // handed to consumers the same one that was written, not a wider one).
      const clamped = clampSpellQueueWindowMs(next);
      persistence.save(clamped);
      setValue(clamped);
    },
    [persistence],
  );

  const ctxValue = useMemo<SpellQueueWindowContextValue>(
    () => ({ spellQueueWindowMs: value, setSpellQueueWindowMs }),
    [value, setSpellQueueWindowMs],
  );

  return <SpellQueueWindowCtx.Provider value={ctxValue}>{children}</SpellQueueWindowCtx.Provider>;
}

export function useSpellQueueWindow(): SpellQueueWindowContextValue {
  const v = useContext(SpellQueueWindowCtx);
  if (v === null) {
    throw new Error("useSpellQueueWindow: must be used within <SpellQueueWindowProvider>");
  }
  return v;
}

export { SPELL_QUEUE_WINDOW_MS_DEFAULT };
